'use client'

import React from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Checkbox, CheckboxProps } from '../Checkbox'
import { Field } from '../Field'
import { Grid } from '../Grid'

export interface CheckboxGroupFieldProps<T extends string | number>
  extends Omit<CheckboxProps, 'label' | 'checked' | 'value' | 'onChange'>,
    SpaceProps {
  /**
   * ID attribute
   */
  id?: string

  /**
   * Class name
   */
  className?: string

  /**
   * Field label
   */
  label?: string

  /**
   * Checkbox options
   */
  options: {
    label: string
    value: T
  }[]

  /**
   * Checkbox columns
   */
  cols?: number

  /**
   * Values
   */
  value: T[]

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]

  /**
   * onChange handler
   */
  onChange?: (value: T[]) => void
}

export const CheckboxGroupFieldInner = <T extends string | number>(
  {
    id,
    className,
    label,
    required,
    options,
    cols = 2,
    errorIcon,
    errorMessages,
    captionIcon,
    captionMessages,
    value,
    ...props
  }: CheckboxGroupFieldProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const [spaceProps, checkboxProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content>
        <Grid mt={3} columns={cols} gap={4}>
          {options.map((option) => (
            <Checkbox
              key={option.value}
              ref={ref}
              label={option.label}
              value={option.value}
              checked={value.includes(option.value)}
              {...checkboxProps}
              onChange={() => {
                if (value.includes(option.value)) {
                  checkboxProps.onChange?.(
                    value.filter((v) => v !== option.value),
                  )
                } else {
                  checkboxProps.onChange?.([...value, option.value])
                }
              }}
            />
          ))}
        </Grid>
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const CheckboxGroupField = React.forwardRef(CheckboxGroupFieldInner) as <
  T extends string | number,
>(
  props: CheckboxGroupFieldProps<T> & {
    ref?: React.ForwardedRef<HTMLLIElement>
  },
) => ReturnType<typeof CheckboxGroupFieldInner>
